import { useState, useEffect, useCallback, useRef } from "react";
import type { ElementContext } from "../components/BugReportDialog";

const OVERLAY_ID = "bug-report-overlay";

const SKIP_FIBER_NAMES = new Set([
  "Slot", "Trigger", "Content", "Portal", "Overlay",
  "Root", "Provider", "Presence", "FocusScope",
  "RemoveScroll", "FocusGuard", "DismissableLayer",
]);

interface ReactComponentInfo {
  name: string | null;
  chain: string[];
  sourceFile: string | null;
}

function isMeaningfulName(name: string): boolean {
  if (!name || name.length <= 2) return false;
  if (name[0] === name[0].toLowerCase()) return false;
  if (SKIP_FIBER_NAMES.has(name)) return false;
  return true;
}

export function getReactFiberComponentInfo(element: Element): ReactComponentInfo {
  try {
    const fiberKey = Object.keys(element).find(k => k.startsWith("__reactFiber$"));
    if (!fiberKey) return { name: null, chain: [], sourceFile: null };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let fiber = (element as any)[fiberKey];
    let name: string | null = null;
    let sourceFile: string | null = null;
    const chain: string[] = [];

    let depth = 0;
    while (fiber && depth < 30) {
      if (typeof fiber.type === "function") {
        const n = fiber.type.displayName || fiber.type.name;
        if (n && isMeaningfulName(n)) {
          if (!name) {
            name = n;
            if (fiber._debugSource) {
              const src = fiber._debugSource;
              const file = (src.fileName ?? "").replace(/^.*?\/src\//, "src/");
              sourceFile = src.lineNumber ? `${file}:${src.lineNumber}` : file;
            }
          }
          chain.push(n);
        }
      }
      fiber = fiber.return;
      depth++;
    }

    return { name, chain: chain.reverse(), sourceFile };
  } catch {
    return { name: null, chain: [], sourceFile: null };
  }
}

export function getDataComponentChain(element: Element): string[] {
  const chain: string[] = [];
  let current: Element | null = element;
  while (current) {
    const name = current.getAttribute("data-component");
    if (name) chain.push(name);
    current = current.parentElement;
  }
  return chain.reverse();
}

function getTestIdAncestors(el: Element): string[] {
  const ids: string[] = [];
  let current = el.parentElement;
  while (current) {
    const tid = current.getAttribute("data-testid");
    if (tid) ids.push(tid);
    current = current.parentElement;
  }
  return ids.reverse();
}

function captureElement(target: Element): ElementContext {
  const rect = target.getBoundingClientRect();
  const testId =
    target.getAttribute("data-testid") ??
    target.closest("[data-testid]")?.getAttribute("data-testid") ??
    "";
  const text = (target.textContent ?? "").trim().slice(0, 80);

  const fiber = getReactFiberComponentInfo(target);
  const dataChain = getDataComponentChain(target);

  const componentName = fiber.name ?? dataChain[dataChain.length - 1] ?? undefined;
  const componentChain = fiber.chain.length > 0 ? fiber.chain : dataChain.length > 0 ? dataChain : undefined;
  const sourceFile = fiber.sourceFile ?? undefined;

  return {
    testId,
    tag: target.tagName.toLowerCase(),
    text,
    rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
    ancestors: getTestIdAncestors(target),
    componentName,
    componentChain,
    sourceFile,
  };
}

export function useBugReport(enabled = true) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [elementContext, setElementContext] = useState<ElementContext | null>(null);
  const [active, setActive] = useState(false);
  const highlightRef = useRef<HTMLElement | null>(null);

  // Toggle inspect mode: Ctrl+Alt+B (Cmd+Alt+B on macOS).
  // Keep legacy Ctrl+Shift+B as fallback when browser doesn't intercept it.
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isPrimaryShortcut = (e.ctrlKey || e.metaKey) && e.altKey && e.code === "KeyB";
      const isLegacyShortcut = e.ctrlKey && e.shiftKey && e.code === "KeyB";

      if (isPrimaryShortcut || isLegacyShortcut) {
        e.preventDefault();
        setActive((prev) => !prev);
      }
      if (e.key === "Escape" && active) {
        setActive(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, enabled]);

  // When active: overlay cursor + highlight hovered element + capture click
  useEffect(() => {
    if (!active) {
      // Cleanup
      const existing = document.getElementById(OVERLAY_ID);
      if (existing) existing.remove();
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      document.body.style.cursor = "";
      return;
    }

    document.body.style.cursor = "crosshair";

    // Create transparent overlay to prevent normal clicks
    const overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "var(--z-debug)",
      cursor: "crosshair",
    });
    document.body.appendChild(overlay);

    // Highlight div
    const highlight = document.createElement("div");
    Object.assign(highlight.style, {
      position: "fixed",
      pointerEvents: "none",
      border: "2px solid #ef4444",
      borderRadius: "4px",
      backgroundColor: "rgba(239,68,68,0.1)",
      zIndex: "calc(var(--z-debug) + 1)",
      transition: "all 0.08s ease-out",
      display: "none",
    });
    document.body.appendChild(highlight);
    highlightRef.current = highlight;

    const onMouseMove = (e: MouseEvent) => {
      // Temporarily hide overlay to find element underneath
      overlay.style.pointerEvents = "none";
      const target = document.elementFromPoint(e.clientX, e.clientY);
      overlay.style.pointerEvents = "";

      if (target && target !== overlay && target !== highlight) {
        const rect = target.getBoundingClientRect();
        Object.assign(highlight.style, {
          display: "block",
          left: `${rect.left - 2}px`,
          top: `${rect.top - 2}px`,
          width: `${rect.width + 4}px`,
          height: `${rect.height + 4}px`,
        });
      } else {
        highlight.style.display = "none";
      }
    };

    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Find element under click
      overlay.style.pointerEvents = "none";
      const target = document.elementFromPoint(e.clientX, e.clientY);
      overlay.style.pointerEvents = "";

      if (target && target !== overlay && target !== highlight) {
        setElementContext(captureElement(target));
        setDialogOpen(true);
      }
      setActive(false);
    };

    overlay.addEventListener("mousemove", onMouseMove);
    overlay.addEventListener("click", onClick);

    return () => {
      overlay.removeEventListener("mousemove", onMouseMove);
      overlay.removeEventListener("click", onClick);
      overlay.remove();
      highlight.remove();
      highlightRef.current = null;
      document.body.style.cursor = "";
    };
  }, [active]);

  const openDialog = useCallback((ctx?: ElementContext) => {
    setElementContext(ctx ?? null);
    setDialogOpen(true);
  }, []);

  return {
    dialogOpen,
    setDialogOpen,
    elementContext,
    active,
    setActive,
    openDialog,
  };
}
